import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchEventPrerequisitesByDomain,
  createEventPrerequisite,
  updateEventPrerequisite,
  deleteEventPrerequisiteUpload,
} from "../services/eventUploadsService";

/**
 * React Query Hook to fetch event prerequisites for a domain
 * GET /event_uploads/event-prerequisites?domain_id=<domain_id>
 */
export const useEventPrerequisites = (domainId, options = {}) => {
  return useQuery(
    ["event-prerequisites-domain", domainId],
    () => fetchEventPrerequisitesByDomain(domainId),
    {
      enabled: Boolean(domainId),
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};

/**
 * React Query Mutation Hook to create event prerequisites for a domain
 * POST /event_uploads/event-prerequisites
 */
export const useCreateEventPrerequisite = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ domain_id, meta_data, uploads }) =>
      createEventPrerequisite({ domain_id, meta_data, uploads }),
    {
      onSuccess: (data, variables) => {
        if (variables?.domain_id) {
          queryClient.invalidateQueries([
            "event-prerequisites-domain",
            variables.domain_id,
          ]);
        }
        if (options.onSuccess) {
          options.onSuccess(data, variables);
        }
      },
      ...options,
    }
  );
};

/**
 * React Query Mutation Hook to update existing event prerequisites
 * PATCH /event_uploads/event-prerequisites/{prerequisite_id}
 */
export const useUpdateEventPrerequisite = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ prerequisite_id, domain_id, meta_data, uploads }) =>
      updateEventPrerequisite(prerequisite_id, { domain_id, meta_data, uploads }),
    {
      onSuccess: (data, variables) => {
        if (variables?.domain_id) {
          queryClient.invalidateQueries([
            "event-prerequisites-domain",
            variables.domain_id,
          ]);
        }
        if (options.onSuccess) {
          options.onSuccess(data, variables);
        }
      },
      ...options,
    }
  );
};

/**
 * React Query Mutation Hook to delete an event prerequisite attachment upload
 * DELETE /event_uploads/event-prerequisite-upload-delete/{upload_id}
 */
export const useDeleteEventPrerequisiteUpload = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    (uploadId) => deleteEventPrerequisiteUpload(uploadId),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries("event-prerequisites-domain");
        if (options.onSuccess) {
          options.onSuccess(data, variables);
        }
      },
      ...options,
    }
  );
};

export default useEventPrerequisites;
